'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { IoChevronDown } from 'react-icons/io5';
import PlusIcon from '@/components/icons/PlusIcon';
import { Button } from '@/components/ui/buttons';
import TrashIcon from '@/components/icons/trash';
import useSearchParams from '@/hooks/useSearchParams';
import useSearchUrlParams from '@/hooks/algolia/useSearchUrlParams';
import { event_filters_used } from '@/utils/gtm';

export default function SkuFilter({ mode = 'category', searchIndexName }) {
  const t = useTranslations('common');
  const [open, setOpen] = useState(false);
  const { getFilterObj, setFilterQueryHandler } = useSearchParams();
  const { urlParams, setFilters, selectedFilterKeys } =
    useSearchUrlParams(searchIndexName);

  const skusFromUrl =
    mode === 'category'
      ? (getFilterObj?.sku?.split(',').filter(Boolean) ?? [])
      : (urlParams?.sku?.split(',').filter(Boolean) ?? []);

  const [inputs, setInputs] = useState(() => {
    const initial = skusFromUrl.length
      ? skusFromUrl.map((val) => ({ id: crypto.randomUUID(), value: val }))
      : [{ id: crypto.randomUUID(), value: '' }];
    return initial;
  });

  useEffect(() => {
    const urlSkus =
      mode === 'category'
        ? (getFilterObj?.sku?.split(',').filter(Boolean) ?? [])
        : (urlParams?.sku?.split(',').filter(Boolean) ?? []);

    if (urlSkus.length === 0) {
      setInputs([{ id: crypto.randomUUID(), value: '' }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getFilterObj?.sku, urlParams?.sku]);

  const didMountRef = useRef(false);
  const userChangedRef = useRef(false);

  const applySkuFilter = useCallback(
    (updatedInputs) => {
      const nonEmpty = updatedInputs.map((i) => i.value.trim()).filter(Boolean);

      if (mode === 'category') {
        setFilterQueryHandler({
          sku: nonEmpty.length ? nonEmpty.join(',') : undefined,
        });
      } else {
        const otherFilters = selectedFilterKeys.filter(
          (k) => !k.startsWith('sku:')
        );
        const skuFilters = nonEmpty.map((val) => `sku:${val}`);
        setFilters([...otherFilters, ...skuFilters]);
      }
    },
    [mode, setFilterQueryHandler, setFilters, selectedFilterKeys]
  );

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }

    const timeout = setTimeout(() => {
      if (userChangedRef.current) {
        const nonEmpty = inputs.map((i) => i.value.trim()).filter(Boolean);
        event_filters_used({
          filter_name: 'SKU',
          filter_value: nonEmpty.join(','),
          filter_value_id: nonEmpty.join(','),
          filter_action: nonEmpty.length ? 'apply' : 'remove',
        });
        userChangedRef.current = false;
      }
      applySkuFilter(inputs);
    }, 500);

    return () => clearTimeout(timeout);
  }, [inputs]);

  // allow used to enter multiple SKUs at once by separating with comma : "SKU1, SKU2, SKU3"
  const handleSkuChange = (id, value) => {
    userChangedRef.current = true;
    if (value.includes(',')) {
      const parts = value
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean);
      if (parts.length > 1) {
        const newInputs = inputs.flatMap((input) =>
          input.id === id
            ? parts.map((val) => ({ id: crypto.randomUUID(), value: val }))
            : [input]
        );
        setInputs(newInputs);
        return;
      }
    }
    const updated = inputs.map((input) =>
      input.id === id ? { ...input, value } : input
    );
    setInputs(updated);
  };

  const addInput = () => {
    userChangedRef.current = true;
    setInputs((prev) => [...prev, { id: crypto.randomUUID(), value: '' }]);
  };

  const removeInput = (id) => {
    userChangedRef.current = true;
    const updated = inputs.filter((input) => input.id !== id);
    setInputs(updated);
    applySkuFilter(updated);
  };

  const activeCount = inputs.filter((i) => i.value.trim()).length;

  return (
    <section className='border-border-50 rounded-2xl border bg-white p-4'>
      <header
        className='flex cursor-pointer items-center justify-between'
        onClick={() => setOpen((o) => !o)}
      >
        <h2 className='font-bold'>{t('SKU')}</h2>

        <div className='flex items-center gap-2'>
          <span className='text-xs text-gray-500'>
            {activeCount} {t('selected')}
          </span>

          <IoChevronDown
            className={`h-4 w-4 text-gray-600 transition-transform duration-300 ${
              open ? 'rotate-180' : ''
            }`}
          />
        </div>
      </header>

      <div
        className={`space-y-2 overflow-hidden transition-all duration-300 ${
          open ? 'mt-3 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        {inputs.map((input) => (
          <div key={input.id} className='flex items-center gap-2'>
            <input
              className='border-border-50 w-full rounded-xl border px-3 py-2'
              placeholder={t('Search by SKU')}
              value={input.value}
              onChange={(e) => handleSkuChange(input.id, e.target.value)}
            />

            {inputs.length > 1 && (
              <button onClick={() => removeInput(input.id)}>
                <TrashIcon className='h-5 w-5 text-gray-600 hover:text-red-500' />
              </button>
            )}
          </div>
        ))}

        <Button
          className='border-primary hover:bg-primary/10 w-full rounded-[10px]! border border-dashed bg-white text-[15px] font-bold disabled:bg-[#788099] disabled:bg-white'
          onClick={addInput}
          // disable add button if there is an empty input to prevent multiple empty inputs
          disabled={inputs.some((i) => i.value.trim() === '')}
        >
          <PlusIcon className='text-primary' />
        </Button>
      </div>
    </section>
  );
}
